import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { Box, Button, Typography } from "@mui/material";
import { styled } from "@mui/material/styles";
import { useDispatch, useSelector } from "react-redux";
import { selectAllSuperlatives, getSuperlativesAction } from "../superlativeSlice";
import { selectAllPlayers, selectPlayer } from "../../player/playerSlice";
import { selectGameId, selectGameObject } from "../../gamestate/gameStateSlice";
import { buildShareCards } from "../utils/buildShareCards";
import { shareCardImage } from "../utils/renderCardToImage";
import { useTrack } from "analytics/hooks";
import { EVENTS } from "analytics/events";
import ShareCard from "./ShareCard";

const SWIPE_THRESHOLD = 40;
const CARD_W = 432;
const CARD_H = 540;

const Root = styled(Box)({
  padding: "20px 16px 28px",
  textAlign: "center",
  display: "flex",
  flexDirection: "column",
  alignItems: "stretch",
  gap: 16,
  maxWidth: 460,
  margin: "0 auto",
  width: "100%",
});

const Header = styled(Typography)({
  fontFamily: "Helvetica Inserat",
  fontSize: "1.6rem",
  letterSpacing: "0.06em",
  color: "#1a7a72",
  fontWeight: 800,
  lineHeight: 1.15,
});

const SubHeader = styled(Typography)({
  fontSize: "0.9rem",
  letterSpacing: "0.18em",
  color: "#777",
  textTransform: "uppercase",
});

const Viewport = styled(Box)({
  position: "relative",
  width: "100%",
  overflow: "hidden",
  borderRadius: 14,
  touchAction: "pan-y",
});

const Track = styled(Box, { shouldForwardProp: (p) => p !== "idx" })(({ idx }) => ({
  display: "flex",
  transform: `translateX(-${idx * 100}%)`,
  transition: "transform 0.45s cubic-bezier(.4,0,.2,1)",
}));

const Slide = styled(Box)({
  flex: "0 0 100%",
  width: "100%",
  display: "flex",
  justifyContent: "center",
  alignItems: "flex-start",
});

const Scaler = styled(Box, { shouldForwardProp: (p) => p !== "scale" })(({ scale }) => ({
  width: CARD_W * scale,
  height: CARD_H * scale,
  position: "relative",
  "& > div": {
    transform: `scale(${scale})`,
    transformOrigin: "top left",
    width: CARD_W,
    height: CARD_H,
  },
}));

const Dots = styled(Box)({
  display: "flex",
  justifyContent: "center",
  gap: 8,
  padding: "6px 0",
});

const Dot = styled("button", { shouldForwardProp: (p) => p !== "active" })(({ active }) => ({
  width: active ? 24 : 10,
  height: 10,
  borderRadius: active ? 5 : "50%",
  background: active ? "#2ec4b6" : "rgba(0,0,0,0.2)",
  border: "none",
  padding: 0,
  cursor: "pointer",
  transition: "all 0.3s ease",
}));

const ShareBtn = styled(Button)({
  background: "#2ec4b6",
  color: "#fff",
  fontWeight: 700,
  fontSize: "1.1rem",
  textTransform: "none",
  borderRadius: 12,
  padding: "14px",
  "&:hover": { background: "#269d91" },
});

const PhoneShareCards = () => {
  const dispatch = useDispatch();
  const gameId = useSelector(selectGameId);
  const player = useSelector(selectPlayer);
  const players = useSelector(selectAllPlayers);
  const superlatives = useSelector(selectAllSuperlatives);
  const game = useSelector(selectGameObject);

  useEffect(() => {
    if (gameId) dispatch(getSuperlativesAction(gameId));
  }, [gameId, dispatch]);

  const [idx, setIdx] = useState(0);
  const [scale, setScale] = useState(1);
  const cardRefs = useRef([]);
  const viewportRef = useRef(null);
  const touchStartRef = useRef(null);
  const track = useTrack();
  const fired = useRef(false);

  useLayoutEffect(() => {
    const el = viewportRef.current;
    if (!el) return;
    const measure = () => {
      const w = el.clientWidth;
      if (w > 0) setScale(Math.min(1, w / CARD_W));
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const cards = useMemo(() => {
    if (!player || !players?.length) return [];
    return buildShareCards({ player, players, superlatives });
  }, [player, players, superlatives]);

  useEffect(() => {
    if (fired.current) return;
    if (!cards?.length) return;
    fired.current = true;
    const rankCard = cards.find((c) => c.variant === "rank");
    const stats = rankCard?.data;
    track(EVENTS.SUPERLATIVES_FINALE_VIEWED, {
      game_id: gameId,
      player_rank: stats?.standings?.find((s) => s.isMe)?.rank ?? 0,
      is_tied: (rankCard?.data?.rankLabel ?? "").startsWith("T-"),
      player_count: players?.length ?? 0,
      final_score: player?.score ?? 0,
      cards_shown_count: cards.length,
    });
  }, [cards, gameId, players, player, track]);

  useEffect(() => {
    if (!cards?.length) return;
    const card = cards[idx];
    if (!card) return;
    const rankCard = cards.find((c) => c.variant === "rank");
    const myRow = rankCard?.data?.standings?.find((s) => s.isMe);
    track(EVENTS.SHARE_CARD_VIEWED, {
      game_id: gameId,
      variant: card.variant,
      card_index: idx,
      cards_total: cards.length,
      player_rank: myRow?.rank ?? 0,
      is_tied: (rankCard?.data?.rankLabel ?? "").startsWith("T-"),
      ...(card.variant === "superlative" && card.data?.superlativeId
        ? { superlative_id: card.data.superlativeId }
        : {}),
    });
  }, [idx, cards, gameId, track]);

  if (cards.length === 0) return null;

  const onShareEvent = (kind, extra = {}) => {
    const card = cards[idx];
    if (!card) return;
    const rankCard = cards.find((c) => c.variant === "rank");
    const myRow = rankCard?.data?.standings?.find((s) => s.isMe);
    const base = {
      game_id: gameId,
      variant: card.variant,
      card_index: idx,
      cards_total: cards.length,
      player_rank: myRow?.rank ?? 0,
      is_tied: (rankCard?.data?.rankLabel ?? "").startsWith("T-"),
      share_method: extra.shareMethod ?? "native_share",
      ...(card.variant === "superlative" && card.data?.superlativeId
        ? { superlative_id: card.data.superlativeId }
        : {}),
    };
    const eventMap = {
      initiated: EVENTS.SHARE_INITIATED,
      completed: EVENTS.SHARE_COMPLETED,
      dismissed: EVENTS.SHARE_DISMISSED,
      failed: EVENTS.SHARE_FAILED,
      downloaded: EVENTS.SHARE_DOWNLOADED,
    };
    const eventName = eventMap[kind];
    if (!eventName) return;
    const payload = kind === "failed"
      ? { ...base, error_name: extra.errorName || "Unknown" }
      : base;
    track(eventName, payload);
  };

  const onShare = async () => {
    const node = cardRefs.current[idx];
    await shareCardImage(node, `groupardy-${cards[idx].variant}.png`, onShareEvent);
  };

  const onTouchStart = (e) => {
    touchStartRef.current = e.touches[0].clientX;
  };

  const onTouchEnd = (e) => {
    if (touchStartRef.current === null) return;
    const delta = e.changedTouches[0].clientX - touchStartRef.current;
    touchStartRef.current = null;
    if (Math.abs(delta) < SWIPE_THRESHOLD) return;
    if (delta < 0 && idx < cards.length - 1) setIdx(idx + 1);
    if (delta > 0 && idx > 0) setIdx(idx - 1);
  };

  const headerText = game?.title || "";

  return (
    <Root>
      <Header>{headerText}</Header>
      <SubHeader>Swipe to pick · tap share</SubHeader>
      <Viewport ref={viewportRef} onTouchStart={onTouchStart} onTouchEnd={onTouchEnd}>
        <Track idx={idx}>
          {cards.map((c, i) => (
            <Slide key={`${c.variant}-${i}`}>
              <Scaler scale={scale}>
                <div>
                  <ShareCard
                    ref={(el) => { cardRefs.current[i] = el; }}
                    variant={c.variant}
                    data={c.data}
                    gameTitle={game?.title}
                    gameDate={game?.createdAt}
                  />
                </div>
              </Scaler>
            </Slide>
          ))}
        </Track>
      </Viewport>
      <Dots>
        {cards.map((_, i) => (
          <Dot key={i} active={i === idx} onClick={() => setIdx(i)} />
        ))}
      </Dots>
      <ShareBtn onClick={onShare}>↗ Share</ShareBtn>
    </Root>
  );
};

export default PhoneShareCards;
